import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { ExecuteResult } from '@/model/common';
import { CustomColumnItem } from '@/business-components/custom-column';
import { hasFeatureFlag } from '@/services';

const useListView = (props?: {
  // 表格多选或单选
  select?: 'multiple' | 'single';
  // 表格唯一建，需要selectData时传入
  rowKey?: string;
  // 下载字段是否包含return_fields
  downloadReturnFields?: boolean;
  // 组装下载参数
  buildDownloadParams?: (params: Record<string, any>) => Record<string, any>;
  // 自定义列配置接口
  customColumnAction?: () => Promise<ExecuteResult<Array<CustomColumnItem>>>;
  // 表格字段映射
  columnMap?: Record<string, any>;
  // 自定义筛选项配置前置钩子
  beforeBuildFilterItemConf?: (item: Record<string, any>) => Record<string, any>;
}) => {
  const listRef = useRef<any>();
  const { downloadReturnFields = false, select = 'multiple', rowKey, customColumnAction, buildDownloadParams, columnMap, beforeBuildFilterItemConf } = props || {};
  // 表格列
  const [tableColumns, setTableColumns] = useState<Array<any>>([]);
  // 被移除的表格列，一般用于feature_flag
  const [removedColumns, setRemovedColumns] = useState<Array<string>>([]);
  // 表格选中的值
  const [selectDataValue, setSelectDataValue] = useState<any>(select === 'multiple' ? [] : null);
  // 表格选中的数据
  const [selectData, setSelectData] = useState<any>(select === 'multiple' ? [] : null);
  // 筛选配置
  const [filterConf, setFilterConf] = useState<any>();
  // 自定义列配置
  const [customConfig, setCustomConfig] = useState<Array<CustomColumnItem>>([]);
  // 接口获取的自定义列
  const [dynamicFields, setDynamicFields] = useState<Array<string>>([]);

  useEffect(() => {
    if (customColumnAction) {
      customColumnAction().then((res: ExecuteResult<Array<CustomColumnItem>>) => {
        const data = res?.data || [];
        setCustomConfig(data);
        // 递归获取自定义列配置
        const dynamicFields: Array<string> = [];
        const getFields = (data: Array<any>) => {
          return data?.forEach((v: any) => {
            const { list, columns } = v;
            if (columns?.length) {
              dynamicFields.push(...columns.map((v: any) => v.name));
            }
            if (list?.length) {
              getFields(list);
            }
          });
        };
        getFields(data);
        setDynamicFields(dynamicFields);
      });
    }
  }, [customColumnAction]);

  // 表格自定义列配置改变
  const handleColumnsChange = useCallback((selectedColumns: any) => {
    const { columns } = selectedColumns;
    setTableColumns(columns);
  }, []);

  // 获取返回字段
  const getReturnFields = useCallback((tableColumns: Array<any>, joinWithComma = true) => {
    const returnFields = tableColumns.map(({ name, returnFieldsSiblings }: any) => {
      if (returnFieldsSiblings) {
        return [name, ...returnFieldsSiblings || []];
      }
      return name;
    }).flat()
      .filter((v: string) => v);
    return joinWithComma ? returnFields.join(',') : returnFields;
  }, []);

  // 获取下载参数
  const getDownloadParams = useCallback(() => {
    const params = {
      ...listRef?.current?.requestParams?.data || {},
      ...(downloadReturnFields ? {
        return_fields: tableColumns.map(({ name }: any) => name).filter((v: string) => v)
          .join(','),
      } : {}),
    };
    return typeof buildDownloadParams === 'function' ? buildDownloadParams(params) : params;
  }, [downloadReturnFields, tableColumns, buildDownloadParams]);

  // 表格选择
  const onOptionalChange = useCallback((_: Event | null, value: any, data?: any) => {
    setSelectDataValue(value);
    if (select === 'multiple') {
      if (rowKey) {
        const tableData = listRef.current?.tableData?.list || [];
        const selectData = tableData.filter((v: any) => value.includes(v[rowKey]));
        setSelectData(selectData);
      }
    } else {
      setSelectData(data || null);
    }
  }, [select, rowKey]);

  // 初始化筛选项配置
  const initFilterConf = useCallback((data: Array<any>) => {
    const filterConf = data?.reduce((map: any, v: any) => {
      if (typeof beforeBuildFilterItemConf === 'function') {
        v = beforeBuildFilterItemConf(v);
      }
      const key = v.filter?.key || v.name;
      const valueType = v.valueType || 'string';
      let options = v.filter;
      // zhCN key为数字时进行转化
      if (v.zhCn && valueType === 'number') {
        v.zhCn = Object.keys(v.zhCn).reduce((map: any, key: string) => ({ ...map, [Number(key)]: v.zhCn[key] }), {});
      }
      // nouseInDataConfig: 不将中文映射应用于select的data
      if (!Boolean(v.nouseInDataConfig) && v.zhCn && Object.keys(v.zhCn).length) {
        const zhCnList = Object.keys(v.zhCn).map((key) => ({ value: valueType === 'number' ? Number(key) : key, label: v.zhCn[key] }));
        options = {
          ...options || {},
          config: {
            data: zhCnList,
            ...(options?.config || {}),
          },
        };
      }
      return {
        ...map,
        ...(options ? {
          [key]: options,
        } : {}),
      };
    }, {});

    setFilterConf(filterConf);
  }, [beforeBuildFilterItemConf]);

  // 初始化表格字段配置
  const initColumnsConf = useCallback((data: Array<any>) => {
    const removedColumns: Array<string> = [];
    data?.forEach((v: any, i: number) => {
      if (v.enableKey && !hasFeatureFlag(v.enableKey)) {
        removedColumns.push(v.name);
      }
      if (v.removeKey && hasFeatureFlag(v.removeKey)) {
        removedColumns.push(v.name);
      }
      if (columnMap?.[v.name]) {
        data[i] = {
          ...v,
          ...columnMap[v.name],
        };
      }
    });

    setRemovedColumns(removedColumns);
  }, [columnMap]);

  const onColumnsLoaded = useCallback((data: Array<any>) => {
    initFilterConf(data);
    initColumnsConf(data);
  }, [initFilterConf, initColumnsConf]);

  const reloadList = useCallback(() => {
    listRef?.current?.loadList();
  }, []);

  const resetPage = useCallback(() => {
    listRef?.current?.setPageNo?.(1);
  }, []);

  return {
    listRef,
    tableColumns,
    removedColumns,
    filterConf,
    selectDataValue,
    selectData,
    customConfig,
    dynamicColumnsFields: dynamicFields,
    setTableColumns,
    getDownloadParams,
    handleColumnsChange,
    reloadList,
    onOptionalChange,
    initFilterConf,
    initColumnsConf,
    onColumnsLoaded,
    getReturnFields,
    resetPage,
  };
};

export default useListView;
